import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Facebook, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { submitEmailSignup } from "@/lib/api";
import { IMG } from "@/lib/images";

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={props.size || 16} height={props.size || 16}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    document.title = "ROADRIG · Coming Soon";
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await submitEmailSignup(email, "coming_soon");
      toast.success("You're on the list. The first ride starts soon.");
      setJoined(true);
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Signup failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="coming-soon-page" className="relative min-h-screen bg-[#0A0A0C] text-white overflow-hidden">
      {/* Background hero image — John on the Road Glide coast ride */}
      <div className="absolute inset-0">
        <img
          src={IMG.RIDE_COAST}
          alt="John riding his Harley Road Glide along the coast"
          style={{ objectPosition: "50% 45%" }}
          className="w-full h-full object-cover hero-zoom"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/35 to-[#0A0A0C]/65" />
      </div>

      {/* Top bar */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-8 flex items-center justify-between">
        <Link to="/" data-testid="cs-logo-link" className="h-display text-2xl">
          ROAD<span className="text-[#F47A20]">RIG</span>
        </Link>
        <Link to="/" data-testid="cs-back-link" className="serial text-[10px] text-white/60 hover:text-white flex items-center gap-2">
          <ArrowLeft size={12} /> Back to Site
        </Link>
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-[80vh] max-w-3xl mx-auto px-6 md:px-12 flex flex-col justify-center text-center py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
        >
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="section-mark" />
            <span className="overline">Launching Soon · Established 2026</span>
            <span className="section-mark" />
          </div>

          {/* Prototype status — replaces countdown */}
          <div data-testid="cs-prototype-status" className="inline-flex flex-col items-center gap-3 mb-8">
            <span className="tactical-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F47A20] animate-pulse" />
              Prototype V1 Coming Soon
            </span>
            <span className="serial text-[11px] text-white/60">
              JOIN THE VIP LIST FOR FIRST ACCESS
            </span>
          </div>

          <h1 className="h-display text-5xl sm:text-7xl lg:text-[8rem]">
            The first ride <br />
            <span className="text-[#F47A20]">starts here.</span>
          </h1>

          <p className="mt-8 text-white/70 text-base sm:text-lg max-w-xl mx-auto">
            ROADRIG is launching the OUTLAW — a premium motorcycle sling bag built for riders who demand more. Sign up for VIP early access.
          </p>

          <form onSubmit={onSubmit} className="mt-12 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <input
              data-testid="cs-email-input"
              type="email"
              required
              placeholder="rider@roadrig.com.au"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-[#1A1A1D]/80 backdrop-blur border border-white/15 rounded-sm px-5 py-4 text-white placeholder:text-white/30 focus:border-[#F47A20] focus:ring-1 focus:ring-[#F47A20] outline-none"
            />
            <button data-testid="cs-email-submit" type="submit" disabled={submitting} className="btn-primary justify-center">
              {submitting ? "Joining..." : joined ? "Joined" : "Join VIP"}
              <ArrowRight size={18} />
            </button>
          </form>

          <p className="serial text-[10px] text-white/40 mt-6">
            VIP EARLY ACCESS · EXCLUSIVE DROPS · NO SPAM
          </p>

          {/* Social */}
          <div className="mt-16 flex justify-center items-center gap-3">
            {[
              { Icon: Instagram, href: "https://instagram.com/roadrig.au", testid: "cs-social-instagram" },
              { Icon: Facebook, href: "https://www.facebook.com/profile.php?id=61591062287084", testid: "cs-social-facebook" },
            ].map(({ Icon, href, testid }) => (
              <a
                key={testid}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={testid}
                className="w-11 h-11 grid place-items-center border border-white/10 rounded-sm text-white/70 hover:text-[#F47A20] hover:border-[#F47A20]/40 transition-all"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Bottom bar */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="serial text-[10px] text-white/40">ROADRIG AUSTRALIA · ESTABLISHED 2026</div>
        <a href="mailto:hello@roadrig.com.au" className="serial text-[10px] text-white/40 hover:text-white">
          JOHN@ROADRIG.COM.AU
        </a>
      </div>
    </div>
  );
}
